import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { cloneDeep, get, isEmpty, isEqual, isNil, xorWith } from 'lodash';
import { BehaviorSubject, delay, distinctUntilChanged, filter, merge, shareReplay } from 'rxjs';
import { map, startWith, throttleTime } from 'rxjs/operators';
import { IDateRangePickerDate } from '../../shared/components/date-range-picker/date-range-picker.interface';
import { TypeFromObs } from '../interfaces/utility-types.interface';
import { doInputGenerateVisibleText } from '../validators/text-editor-required.validator';

type TFormGroupValue = TypeFromObs<AdvancedFormGroup['lastRecordedValueUntilFocused$']>;

export class AdvancedFormGroup<
  Control extends {
    [K in keyof Control]: AbstractControl;
  } = any,
> extends FormGroup<Control> {
  /** @description
   * Tracks whether any input in the form group ever focused at least once or not. Once any input is focused, we will assume that the user have taken an action and form data initialization is done.
   * This is expected to be assigned by giving trackFocusedOnce directive to the form element and tracking firstFocus event output
   */
  public isFocusedOnce = new BehaviorSubject<boolean>(false);

  /** @description
   * Has the value of the form group and keeps the value up to date until isFocusedOnce is set to true
   * */
  public readonly lastRecordedValueUntilFocused$ = merge(
    this.valueChanges,
    this.isFocusedOnce.pipe(distinctUntilChanged()),
  ).pipe(
    filter(() => !this.isFocusedOnce.value),
    throttleTime(100, undefined, { leading: true, trailing: true }),
    map(() => cloneDeep(this.value)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly lastRecordedValueUntilFocused = toSignal(this.lastRecordedValueUntilFocused$);

  /** @description
   * Compares the current value with the lastRecordedValueUntilFocused and decides whether the form has unsaved changes or not.
   * Once the value is changed, if the value is changed back to match the lastRecordedValueUntilFocused, isThereUnsavedChanges$ will be set to false
   * */
  public readonly isThereUnsavedChanges$ = this.valueChanges.pipe(
    filter(() => this.isFocusedOnce.value),
    throttleTime(100, undefined, { leading: true, trailing: true }),
    delay(50),
    map((currentValue) => !this.areFormValuesEqual(currentValue, this.lastRecordedValueUntilFocused())),
    startWith(false),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly isThereUnsavedChanges = toSignal(this.isThereUnsavedChanges$, { initialValue: false });

  private areFormValuesEqual(currentValue: TFormGroupValue, lastRecordedValueUntilTouched: TFormGroupValue): boolean {
    return Object.keys(currentValue).every((key: keyof TFormGroupValue) => {
      const currentControlValue = currentValue[key];
      const lastRecordedValueUntilTouchedControlValue = lastRecordedValueUntilTouched[key];
      const isDateInput: boolean =
        currentControlValue &&
        typeof currentControlValue === 'object' &&
        'startDate' in currentControlValue &&
        'endDate' in currentControlValue;

      if (isDateInput) {
        return (
          (currentControlValue as IDateRangePickerDate)?.startDate?.unix() ===
            (lastRecordedValueUntilTouchedControlValue as IDateRangePickerDate)?.startDate?.unix() &&
          (currentControlValue as IDateRangePickerDate)?.endDate?.unix() ===
            (lastRecordedValueUntilTouchedControlValue as IDateRangePickerDate)?.endDate?.unix()
        );
      }

      return this.valuesAreEqualOrBothEmpty(currentControlValue, lastRecordedValueUntilTouchedControlValue);
    });
  }

  private getIsInputEmpty(value: unknown): boolean {
    return (
      isNil(value) ||
      isEmpty(value) ||
      !value ||
      (get(value, 'content.length') === 1 && !get(value, 'content.0.content')) ||
      (typeof value === 'string' && !doInputGenerateVisibleText(value))
    );
  }

  private valuesAreEqualOrBothEmpty(value1: unknown, value2: unknown): boolean {
    const isValue1Empty = this.getIsInputEmpty(value1);
    const isValue2Empty = this.getIsInputEmpty(value2);

    if (isValue1Empty && isValue2Empty) {
      return true;
    }

    if (Array.isArray(value1) && Array.isArray(value2)) {
      return value1.length === value2.length && !xorWith(value1, value2, isEqual).length;
    }

    const isValue1Json = value1 !== null && !Array.isArray(value1) && typeof value1 === 'object';

    if (!isValue1Json) {
      return isEqual(value1, value2);
    }

    const isValue2Json = value2 !== null && !Array.isArray(value2) && typeof value2 === 'object';

    return (
      isValue2Json &&
      Object.keys(value1).length === Object.keys(value2).length &&
      Object.keys(value1).every((key) => {
        const value1Sub = (value1 as Record<string, unknown>)[key];
        const value2Sub = (value2 as Record<string, unknown>)[key];

        return this.valuesAreEqualOrBothEmpty(value1Sub, value2Sub);
      })
    );
  }
}
